import EditorialBody from 'components/EditorialBody';
import React, { useEffect, useState, ChangeEvent, useMemo, useCallback, useRef } from 'react';
import PortableTextComponent from 'components/EditorialPortableText';
import { urlFor } from 'services/server/sanity/components/Image';
import { debounce } from 'lodash';
import { addMonths, addDays } from 'date-fns';
import { useRouter } from 'next/router';
import { Combobox, Popover } from '@headlessui/react';
import { FaChevronDown, FaChevronUp, FaArrowUp, FaArrowDown } from 'react-icons/fa6';
import { FaSackDollar } from 'react-icons/fa6';
import { FiActivity } from 'react-icons/fi';
import { IoCopy, IoCheckmark } from 'react-icons/io5';
import {
  CalendarIcon,
  PencilIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  XIcon,
} from '@heroicons/react/outline';
import { LuScale3D } from 'react-icons/lu';
import { TbZoomMoney } from 'react-icons/tb';
import { BsGraphUpArrow, BsGraphDownArrow } from 'react-icons/bs';
import { IoArrowRedoOutline } from 'react-icons/io5';
import { FaLock } from 'react-icons/fa';

import Head from 'components/Head';
import Link from 'components/Link';
import PageTitle from 'components/PageTitle';
import MarketDataStatus from 'components/MarketDataStatus';
import useFlowAccess from 'hooks/useFlowAccess';
import { StrategyStatsSkeleton, StrategyChartTableSkeleton } from './StrategySkeleton';

import TickerDetailsCard from 'screens/TickerResearchPage/TickerDetailsCard';
// import NavBar from 'components/Website/Navbar';
import FloatingBox from 'components/auth/FloatingBox';
import Footer from 'layouts/Footer';
import useViewer from 'hooks/useViewer';
import { hasDashboardAccess } from 'services/client/token';
import { AuthStatus } from 'constants/auth';
import {
  PRICING_PAGE,
  OPC_PAGE,
  // AFFILIATE_PROGRAM_ARTICLE_PAGE,
  MASTER_CLASS_ARTICLE_PAGE,
  HOME_PAGE,
  OPTION_FLOW_PAGE,
  LOGIN_PAGE,
  SIGNUP_PAGE,
  getOpcStrategyPage,
  PageIcons,
} from 'constants/pages';
import { OptionChain, StrikesForExpiration } from 'constants/opc';

import { searchTickerFilter } from 'utils/shared/tickers/searchTickerFilter';
import { fetchOpcData } from 'utils/client/protobuf';
import { StrategyBuilder } from 'utils/shared/quantlib/StrategyBuilder';
import {
  CALL,
  PUT,
  OptionType,
  dateStringToMarketClose,
  expirationToDate,
} from 'utils/shared/quantlib/utils';
import {
  calculatePremium,
  calculateTimeToExpirationInYears,
  dateToDateString,
} from 'utils/shared/quantlib/BlackScholes';
import { longPut } from 'utils/shared/optionProfitCalculator/longPut';
import { longCall } from 'utils/shared/optionProfitCalculator/longCall';
import { shortPut } from 'utils/shared/optionProfitCalculator/shortPut';
import { shortCall } from 'utils/shared/optionProfitCalculator/shortCall';

import { strategies } from 'utils/shared/optionProfitCalculator';
import { getPremium, DEFAULT_STOCK_POSITION } from 'utils/shared/optionProfitCalculator/utils';
import { DialogComponent } from 'components/Dialog';
import Modal from 'components/Modal/HeadlessUiModal';

import classNames from 'styles/utils/classNames';

import { roundToDecimals } from 'utils/shared/number/roundToDecimals';

import { allStrategies, type Strategy } from 'constants/opc_data';
import { Icon } from 'screens/OptionsProfitCalculator/Icons';
import { StrategyCard } from './StrategyCard';
import PayoffChart from './Chart';
import DateDropdown from './DateMenu';
import { GreekLetters } from './GreekLetters';
import { useTimeScrub } from './useTimeScrub';
import { Props, SavedOpcStrategy, StockOptionParamters, UnderlyingStockParameters } from './types';
import { RightSidePanel } from './RightPanel';
import { ListStrategyModal } from './ListStrategyModal';
import { SaveStrategyModal } from './SaveStrategyModal';
import PositionsRefreshControl from './PositionsRefreshControl';
import { getTickerList } from 'services/client/getTickerList';
import LayoutToggleButton from 'components/LayoutToggleButton';
import useSinglePaneLayout, { getSinglePaneClass } from 'hooks/useSinglePaneLayout';
import { trackEvent } from 'services/client/analytics';
import { OPC_EXPAND_PANE, OPC_RESTORE_PANE } from 'services/client/analytics/events';
// import TopBanner from 'components/TopBanner/Banner';
import { useArchiveOpcSavedStrategyMutation } from 'types/generated/client';
import { StrategyBuilderParams } from 'utils/shared/optionProfitCalculator/types';
import StrikeCombobox from './StrikeCombobox';
import { getAvailableStrikes } from './utils';
import VolatilityControl from 'screens/OptionsProfitCalculatorStrategy/VolatilityControl';
import PricingEngineStatus from 'screens/OptionsProfitCalculatorStrategy/PricingEngineStatus';
import { useStrategyVolatility } from 'screens/OptionsProfitCalculatorStrategy/useStrategyVolatility';
import TableValue, {
  getTableValue,
  TableSetting,
} from 'screens/OptionsProfitCalculatorStrategy/tableDisplay';

const DEFAULT_TICKER = 'SPY';
const TITLE_SUFFIX = 'Options Strategy';

type TickerType = {
  t: string;
  n: string;
  s: string;
  i: string;
};

const NAVIGATION_ITEMS = [
  // {
  //   name: 'Product',
  //   href: '#',
  //   current: true,
  //   isMenu: true,
  //   subMenu: [
  //     {
  //       name: 'Complete Trading Toolkit',
  //       href: `${HOME_PAGE}#${DASHBOARD_SECTION}`,
  //       current: true,
  //     },
  //     { name: 'Technical Analysis System', href: TECHNICAL_ANALYSIS_LANDING_PAGE, current: true },
  //     { name: 'Options Profit Calculator', href: OPC_PAGE, current: true },
  //   ],
  // },
  { name: 'Home', href: HOME_PAGE, current: false },
  { name: 'Pricing', href: PRICING_PAGE, current: false },
  { name: 'Free OPC', href: OPC_PAGE, current: false },
  // { name: 'Affiliate Program', href: AFFILIATE_PROGRAM_ARTICLE_PAGE, current: false },
  { name: 'Free Masterclass', href: MASTER_CLASS_ARTICLE_PAGE, current: false },
  { name: 'Free Options Flow', href: OPTION_FLOW_PAGE, current: false },
];

const optionsToAdd = [
  {
    name: 'Long Call',
    function: longCall,
    type: 'option',
  },
  {
    name: 'Short Call',
    function: shortCall,
    type: 'option',
  },
  {
    name: 'Long Put',
    function: longPut,
    type: 'option',
  },
  {
    name: 'Short Put',
    function: shortPut,
    type: 'option',
  },
  {
    name: 'Buy stock',
    type: 'stock',
  },
  {
    name: 'Sell stock',
    type: 'stock',
  },
];

const keyNameForTableSetting = {
  [TableSetting.ProfitAmount]: 'profit',
  [TableSetting.ProfitPercent]: 'profitPercent',
  [TableSetting.ContractValue]: 'contractValue',
  [TableSetting.PercentOfMaxRisk]: 'percentOfMaxRisk',
  [TableSetting.Delta]: 'delta',
  [TableSetting.Gamma]: 'gamma',
  [TableSetting.Theta]: 'theta',
  [TableSetting.Vega]: 'vega',
  [TableSetting.Rho]: 'rho',
};

const tableSettingToKeyName = (key: TableSetting) => {
  return keyNameForTableSetting[key];
};

const tableSettingDisplayNames: Record<TableSetting, string> = {
  [TableSetting.ProfitAmount]: 'Profit / Loss $',
  [TableSetting.ProfitPercent]: 'Profit / Loss %',
  [TableSetting.ContractValue]: 'Contract Value',
  [TableSetting.PercentOfMaxRisk]: ' % of Max Risk',
  [TableSetting.Delta]: 'Δ Delta',
  [TableSetting.Gamma]: 'Γ Gamma',
  [TableSetting.Theta]: 'Θ Theta',
  [TableSetting.Vega]: 'ν Vega',
  [TableSetting.Rho]: 'ρ Rho',
};

const tableSettingShortDisplayNames: Record<TableSetting, string> = {
  [TableSetting.ProfitAmount]: 'Profit $',
  [TableSetting.ProfitPercent]: 'Profit %',
  [TableSetting.ContractValue]: 'Contract Value',
  [TableSetting.PercentOfMaxRisk]: ' % of Max Risk',
  [TableSetting.Delta]: 'Δ Delta',
  [TableSetting.Gamma]: 'Γ Gamma',
  [TableSetting.Theta]: 'Θ Theta',
  [TableSetting.Vega]: 'ν Vega',
  [TableSetting.Rho]: 'ρ Rho',
};

const DEMO_CHAIN_INDEX = 5;
const UNDERLYING_STOCK_POSITION_STEPPER = DEFAULT_STOCK_POSITION;
// Live-updates cadence for paid members; also acts as a shared floor between
// live and manual refreshes — clicks within this window spin visually but skip
// the network fetch.
const REFRESH_INTERVAL_MS = 60 * 1000;
const FAKE_SPIN_MS = 600;

const toPercent = (value: number) => value * 100;
const toDecimal = (value: number) => value / 100;
const displayVolatility = (volatility: number) => toPercent(volatility);
const storedVolatility = (volatility: number) => toDecimal(volatility);
const displayPriceRange = (price: number) => toPercent(price);
const storedPriceRange = (price: number) => toDecimal(price);

const stockSortFunction = (a: StockOptionParamters, b: StockOptionParamters) => {
  return a.strike - b.strike;
};

const strategyBuilderSortFunction = (a: StrategyBuilderParams, b: StrategyBuilderParams) => {
  return a.strike - b.strike;
};

const useStockOptions = (
  options: StockOptionParamters[] = [],
  stocks: UnderlyingStockParameters[] = [],
) => {
  const [stockOptions, setStockOptions] = useState<StockOptionParamters[]>(options);
  const [underlyingStocks, setUnderlyingStocks] = useState<UnderlyingStockParameters[]>(stocks);
  const [lastAddedIndex, setLastAddedIndex] = useState<any>(null);

  // const insertStockOption = (option: StockOptionParamters[]) =>
  //   setStockOptions((prev) => [...prev, ...option]);
  const insertStockOption = (option: StockOptionParamters[]) => {
    setStockOptions((prev) => {
      const newOptions = [...option, ...prev];
      setLastAddedIndex(0);
      return newOptions;
    });

    setTimeout(() => {
      setLastAddedIndex(null);
    }, 10_000);
  };
  const removeStockOptionAtIndex = (index: number) => {
    setLastAddedIndex(null);
    setStockOptions((prev) => {
      const copy = [...prev];
      copy.splice(index, 1);
      return copy;
    });
  };
  const updateStockOptionAtIndex = (index: number, option: StockOptionParamters) => {
    setStockOptions((prev) => {
      const copy = [...prev];
      copy[index] = option;
      return copy;
    });
  };
  const removeAllOptions = () => {
    setLastAddedIndex(null);
    setStockOptions([]);
  };

  const insertUnderlyingStock = (stock: UnderlyingStockParameters[]) =>
    setUnderlyingStocks((prev) => [...prev, ...stock]);
  const removeUnderlyingStockAtIndex = (index: number) => {
    setUnderlyingStocks((prev) => {
      const copy = [...prev];
      copy.splice(index, 1);
      return copy;
    });
  };
  const updateUnderlyingStockAtIndex = (index: number, option: UnderlyingStockParameters) => {
    setUnderlyingStocks((prev) => {
      const copy = [...prev];
      copy[index] = option;
      return copy;
    });
  };
  const removeAllUnderlyingStocks = () => setUnderlyingStocks([]);

  return {
    // Options
    stockOptions,
    setStockOptions,
    lastAddedIndex,
    insertStockOption,
    removeStockOptionAtIndex,
    updateStockOptionAtIndex,
    removeAllOptions,
    // Stocks
    underlyingStocks,
    setUnderlyingStocks,
    insertUnderlyingStock,
    removeUnderlyingStockAtIndex,
    updateUnderlyingStockAtIndex,
    removeAllUnderlyingStocks,
    sortStockOptions: () => {
      setStockOptions((prev) => prev.toSorted(stockSortFunction));
    },
  };
};

const formatStrategyName = (name: string) => {
  return name
    ?.split('-')
    ?.map((word) => word?.charAt(0)?.toUpperCase() + word?.slice(1))
    ?.join(' ');
};

const isGreekTableSetting = (tableSetting: TableSetting) => {
  return (
    tableSetting === TableSetting.Delta ||
    tableSetting === TableSetting.Gamma ||
    tableSetting === TableSetting.Theta ||
    tableSetting === TableSetting.Vega ||
    tableSetting === TableSetting.Rho
  );
};

const generateMetaDescription = (strategyName: string) => {
  return `The ${strategyName} profit calculator for the ${strategyName} options strategy with real-time option price data for any ticker. Visualize the payoff of ${strategyName} with an interactive chart and table. Learn about ${strategyName} from the #1 website for stock options education.`;
};

const calculateTimeToExpiration = (expirationDate: Date) => {
  return calculateTimeToExpirationInYears(expirationDate);
};

const TopNav = () => {
  return (
    <>
      <div className="hidden h-12 w-full items-center justify-between border-b border-palette-border px-2 pl-[60px] md:flex">
        <div>&nbsp;</div>
        <div className="flex items-center gap-6">
          {NAVIGATION_ITEMS.map((item) => (
            <Link key={item.name} href={item.href} className={classNames('text-xs font-semibold')}>
              {item.name}
            </Link>
          ))}
        </div>
        <div className="flex items-center gap-4">
          <Link href={LOGIN_PAGE} className="text-xs font-semibold">
            Log in
          </Link>
          <Link
            href={SIGNUP_PAGE}
            className="rounded bg-brand-green-secondary px-2 py-1 text-center text-xs font-semibold"
          >
            Get Started
          </Link>
        </div>
      </div>
    </>
  );
};

/**
 * Converts StrategyBuilderParams to StockOptionParamters
 * Handles the conversion from Date objects to date strings
 */
const convertStrategyParamsToStockOptions = (
  strategyParams: StrategyBuilderParams[],
): StockOptionParamters[] => {
  return strategyParams.map((param) => ({
    premium: param.premium,
    numberOfContracts: param.numberOfContracts,
    expirationDateString: dateToDateString(param.expirationDate),
    expirationKey: param.expirationKey,
    strike: param.strike,
    type: param.type,
    position: param.position,
  }));
};

export default function OptionsProfitCalculatorStrategy({
  strategyDefinition,
  content,
  jsonLd,
}: Props) {
  const router = useRouter();
  const {
    strategyType,
    ticker: urlTicker,
    expiration: urlExpiration,
    strike: urlStrike,
    size: urlSize,
  } = router.query;
  const ticker: string = router.isReady ? (urlTicker as string | undefined) || DEFAULT_TICKER : '';

  const [tickerData, setTickerData] = useState<TickerType[]>([]);
  const [searchResults, setSearchResults] = useState<TickerType[]>([]);
  const [isEditingOption, setIsEditingOption] = useState(false);
  const [strategyName, setStrategyName] = useState('');

  const viewer = useViewer();
  const flowAccess = useFlowAccess();
  const isUserSession = viewer.status === AuthStatus.User;
  const hasPaidAccess = isUserSession && hasDashboardAccess(viewer);

  const [tableSetting, setTableSetting] = useState(TableSetting.ProfitAmount);
  const [spot, setSpot] = useState(0);
  const [riskFreeRate, setRiskFreeRate] = useState(0);
  const [chain, setChain] = useState<OptionChain>(undefined);
  const [priceRangePercentDecimal, setPriceRangePercentDecimal] = useState(0.1);
  const [priceRangeMaxPercentDecimal, _setPriceRangeMaxPercentDecimal] = useState(0.5);
  const [isBusy, setIsBusy] = useState(true);
  const [isFakeSpinning, setIsFakeSpinning] = useState(false);
  const lastRefreshAtRef = useRef(0);
  const fakeSpinTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [strategyData, setStrategyData] = useState<Strategy[]>([
    allStrategies.find((s) => s.slug === strategyDefinition?.slug) ||
      allStrategies.find((s) => s.slug === strategyType) ||
      allStrategies.find((s) => s.slug === 'long-call') ||
      allStrategies[0],
  ]);
  const [isOpen, setIsOpen] = useState(false);
  const [_optionToAdd, _setOptionToAdd] = useState(optionsToAdd[0].name);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [isListStrategyModalOpen, setIsListStrategyModalOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isLiveUpdatesEnabled, setIsLiveUpdatesEnabled] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isMobilePositionsModalOpen, setIsMobilePositionsModalOpen] = useState(false);
  const [mobileEditingOptionIndex, setMobileEditingOptionIndex] = useState<number | null>(null);
  const hasInitializedFromUrl = useRef(false);
  const splitLayout = useSinglePaneLayout();

  const handleTogglePositionsPane = useCallback(() => {
    if (splitLayout.isExpanded('top')) {
      splitLayout.restore();
      trackEvent(OPC_RESTORE_PANE, { pane: 'top', reason: 'user_toggle', module: 'positions' });
    } else {
      splitLayout.expand('top');
      trackEvent(OPC_EXPAND_PANE, { pane: 'top', module: 'positions' });
    }
  }, [splitLayout]);

  const handleSetBottomExpanded = useCallback(
    (expanded: boolean, currentModule: string, reason: 'user_toggle' | 'ineligible_widget') => {
      if (expanded) {
        splitLayout.expand('bottom');
        trackEvent(OPC_EXPAND_PANE, { pane: 'bottom', module: currentModule });
      } else {
        splitLayout.restore();
        trackEvent(OPC_RESTORE_PANE, { pane: 'bottom', reason, module: currentModule });
      }
    },
    [splitLayout],
  );

  const [archiveStrategyMutation] = useArchiveOpcSavedStrategyMutation();
  /**
   * @todo if you get a new active strategy, set it's parameters as the stock options and underlying stocks
   */

  const {
    stockOptions,
    setStockOptions,
    underlyingStocks,
    lastAddedIndex,
    insertStockOption,
    insertUnderlyingStock,
    updateStockOptionAtIndex,
    removeStockOptionAtIndex,
    setUnderlyingStocks,
    updateUnderlyingStockAtIndex,
    removeUnderlyingStockAtIndex,
    sortStockOptions,
  } = useStockOptions();

  const search = (query: string) => {
    if (query.length >= 1) {
      const results = searchTickerFilter(query, tickerData);
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  };

  const performRefresh = useCallback(
    debounce((ticker: string) => {
      lastRefreshAtRef.current = Date.now();
      setIsBusy(true);
      fetchOpcData(ticker)
        .then((data) => {
          if (data?.rates?.['1y']) {
            setRiskFreeRate(parseFloat((data?.rates?.['1y'] / 100).toFixed(4)));
          }

          if (!data.chains) {
            return;
          }

          setChain(data?.chains);
          const newSpot = data?.stocks?.[0]?.price || 0;
          setSpot(parseFloat(newSpot.toFixed(2)));

          // Trigger refresh for TickerDetailsCard
          setRefreshTrigger((prev) => prev + 1);
        })
        .catch((error) => {
          console.error('Error refreshing data:', error);
        })
        .finally(() => {
          setIsBusy(false);
        });
    }, 1000),
    [],
  );

  // Manual/auto entry point. Within REFRESH_INTERVAL_MS of the last fetch we
  // still show a brief spin so the click feels acknowledged, but skip the
  // network call.
  const refreshData = useCallback(
    (ticker: string) => {
      if (Date.now() - lastRefreshAtRef.current < REFRESH_INTERVAL_MS) {
        if (fakeSpinTimerRef.current) clearTimeout(fakeSpinTimerRef.current);
        setIsFakeSpinning(true);
        fakeSpinTimerRef.current = setTimeout(() => {
          setIsFakeSpinning(false);
          fakeSpinTimerRef.current = null;
        }, FAKE_SPIN_MS);
        return;
      }
      performRefresh(ticker);
    },
    [performRefresh],
  );

  useEffect(() => {
    return () => {
      if (fakeSpinTimerRef.current) clearTimeout(fakeSpinTimerRef.current);
    };
  }, []);

  const fetchNewChain = debounce((ticker: string, slug?: string) => {
    setIsBusy(true);
    fetchOpcData(ticker)
      .then((data) => {
        if (data?.rates?.['1y']) {
          setRiskFreeRate(parseFloat((data?.rates?.['1y'] / 100).toFixed(4)));
        }

        if (!data.chains) {
          return;
        }

        setChain(data?.chains);
        const spot = data?.stocks?.[0]?.price || 0;

        let strategyFunction = longCall;

        if (slug) {
          const strategyMatchingSlug = strategies.find((s) => s.slug === slug)?.function;
          if (strategyMatchingSlug) {
            strategyFunction = strategyMatchingSlug;
          }
        }

        let targetExpirationKey;
        if (urlExpiration) {
          const [year, month, day] = (urlExpiration as string).split('-').map(Number);
          targetExpirationKey = data?.chains[ticker]?.entries?.find(
            (entry: StrikesForExpiration) =>
              entry.ey === year && entry.em === month && entry.ed === day,
          )?.e;
        }

        let targetStrike = urlStrike ? Number(urlStrike) : undefined;

        // Validate strike exists in the chain at the target expiration
        if (targetStrike && targetExpirationKey) {
          const strikesAtExpiration = data?.chains[ticker]?.entries?.find(
            (e: StrikesForExpiration) => e.e === targetExpirationKey,
          );
          if (!strikesAtExpiration?.c?.[targetStrike]) {
            // Strike doesn't exist in chain, fall back to ATM
            targetStrike = undefined;
          }
        }

        const contractMultiplier = urlSize ? Number(urlSize) : 1;

        const initalStrategy = strategyFunction({
          chain: data?.chains[ticker]?.entries,
          spot,
          expirationKey: targetExpirationKey,
          expirationDate: targetExpirationKey
            ? dateStringToMarketClose(urlExpiration as string)
            : undefined,
          strike: targetStrike,
          contractMultiplier,
        });

        setStockOptions(
          convertStrategyParamsToStockOptions(
            initalStrategy.optionPositions.sort(strategyBuilderSortFunction) || [],
          ),
        );
        setUnderlyingStocks(initalStrategy.stockPositions);
        setSpot(parseFloat(spot.toFixed(2)));

        // Clean URL params after using them for initial load (only once)
        if (!hasInitializedFromUrl.current && (urlExpiration || urlStrike || urlSize)) {
          hasInitializedFromUrl.current = true;
          router.replace(router.asPath.split('?')[0], undefined, { shallow: true });
        }
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
      })
      .finally(() => {
        setIsBusy(false);
      });
  }, 1000);

  useEffect(() => {
    if (ticker && strategyType) {
      fetchNewChain(ticker, strategyType as string);
    }
  }, [ticker, strategyType]);

  // Live-updates effect — paid members only. Free/anonymous users must refresh
  // manually (rate-limited in PositionsRefreshControl).
  useEffect(() => {
    if (!hasPaidAccess || !isLiveUpdatesEnabled || !ticker) return;

    const interval = setInterval(() => {
      refreshData(ticker);
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [hasPaidAccess, isLiveUpdatesEnabled, ticker, refreshData]);

  const {
    ivSummary,
    isManualVolatility,
    originalStrategyVolatility,
    volatility,
    setVolatility,
    resetVolatility,
  } = useStrategyVolatility(stockOptions, spot, riskFreeRate);

  // The IV slider spans 3× the solved strategy IV (the 1x/2x markers), widened to fit a larger
  // manual estimate and capped at the solver's 500% ceiling; with no solved IV it spans 0.1–500%.
  const volatilitySlider = useMemo(() => {
    const valuePercent = volatility == null ? 0.1 : displayVolatility(volatility);
    const maxPercent =
      originalStrategyVolatility == null
        ? 500
        : Math.min(500, Math.max(displayVolatility(originalStrategyVolatility) * 3, valuePercent));
    return {
      valuePercent,
      maxPercent,
      fillPercent: Math.min(100, (valuePercent / maxPercent) * 100),
    };
  }, [volatility, originalStrategyVolatility]);

  const results = useMemo(() => {
    if (
      !stockOptions ||
      stockOptions.length === 0 ||
      spot <= 0 ||
      volatility == null ||
      volatility <= 0 ||
      ivSummary.impliedVolatilities.some((result) => result.reason === 'invalid-input')
    ) {
      return undefined;
    }

    const strategyOptionParams = stockOptions.map((option) => {
      return {
        ...option,
        volatility: volatility,
        spot: spot,
        riskFreeRate: riskFreeRate,
        priceRangePercentDecimal: priceRangePercentDecimal,
      };
    });

    const strategyBuilder = new StrategyBuilder({
      strategyOptionParams,
      currentSpot: spot,
      priceRangePercentDecimal,
      priceRangeMaxPercentDecimal,
      underlyingStockPositions: underlyingStocks,
    });
    return strategyBuilder;
  }, [
    stockOptions,
    underlyingStocks,
    volatility,
    spot,
    riskFreeRate,
    priceRangePercentDecimal,
    ivSummary,
  ]);

  // Multi-expiry aware. The default payoff graph is drawn at the NEAREST (front)
  // leg expiry — the conventional "at expiration" diagram, and identical to the
  // single-expiry case (nearest === latest). The time slider (below) instead
  // extends to the LATEST leg expiry, so a calendar/diagonal can be scrubbed
  // *through* the front-leg expiration while longer-dated legs stay live.
  let nearestExpirationDate: Date | undefined;
  let latestExpirationDate: Date | undefined;

  stockOptions.forEach((option) => {
    const optionExpirationDate = dateStringToMarketClose(option.expirationDateString);
    const ms = optionExpirationDate.getTime();
    if (!nearestExpirationDate || ms < nearestExpirationDate.getTime()) {
      nearestExpirationDate = optionExpirationDate;
    }
    if (!latestExpirationDate || ms > latestExpirationDate.getTime()) {
      latestExpirationDate = optionExpirationDate;
    }
  });

  const strategyValues = useMemo(
    () => results?.getFullStrategy(nearestExpirationDate || addMonths(new Date(), 1)),
    [results],
  );

  const hasErraticBehavior = strategyValues?.hasErraticBehavior;
  const maxProfitLoss = strategyValues?.maxProfitLoss;
  const payoffTable = strategyValues?.payoffTable;
  const payoffGraph = strategyValues?.payoffGraph;
  const probabilityOfProfit = strategyValues?.probabilityOfProfit;
  const breakEvenPoints = strategyValues?.breakEvenPoints;
  const maxProfit = maxProfitLoss?.maxProfit;
  const maxLoss = maxProfitLoss?.maxLoss;
  const strategyMetrics = strategyValues?.strategyMetrics;
  const normalizedProfitFactor =
    volatility == null
      ? 0
      : StrategyBuilder.calculateNormalizedProfitFactor({
          maxProfit: maxProfit || 0,
          maxLoss: maxLoss || 0,
          probabilityOfProfit: probabilityOfProfit || 0,
          spot: spot,
          volatility,
        });

  // --- Draggable evaluation-time scrubbing -------------------------------
  // Max profit/loss + probability of profit are expiration concepts and stay
  // fixed; only the payoff curve, breakevens, and greeks move with time. The
  // kernel is already built inside `results`, so each frame is a ~sub-3ms sweep.
  //
  // Consume the exact grid `getFullStrategy` used for `payoffGraph` so the live
  // (scrubbed) curve and the dotted "at expiration" reference are guaranteed
  // index-aligned in PayoffChart (which zips them by position). Re-deriving the
  // bounds locally would line up today by coincidence and silently drift if
  // either path's rounding/step count ever changed.
  const [scrubNowMs] = useState(() => Date.now());
  const fallbackExpiration = addMonths(new Date(), 1);
  const expirationMs = (latestExpirationDate || fallbackExpiration).getTime();
  const defaultEvalMs = (nearestExpirationDate || fallbackExpiration).getTime();
  const scrubGrid = strategyValues?.graphGrid;
  const timeScrub = useTimeScrub({
    results,
    spot,
    lowerBound: scrubGrid?.lowerBound ?? spot,
    upperBound: scrubGrid?.upperBound ?? spot,
    steps: scrubGrid?.steps ?? 500,
    expirationMs,
    nowMs: scrubNowMs,
    defaultEvalMs,
  });
  const canScrubTime = expirationMs > scrubNowMs;

  // The slider drives the PAYOFF only — the curve, its breakevens, and the
  // "P/L at spot" readout all reflect the selected date.
  const chartData = timeScrub.liveCurve.length ? timeScrub.liveCurve : payoffGraph || [];
  const chartBreakEvens = timeScrub.liveCurve.length
    ? timeScrub.liveBreakEvens
    : breakEvenPoints || [];
  // Greeks deliberately do NOT follow the slider — they always describe the
  // position as it stands today. Previously the default view showed today's
  // greeks while the thumb rested at expiration, so the first drag snapped them
  // to the near-expiry regime (where 1/√T gamma/theta are genuinely enormous)
  // and read as a jarring jump. Holding them at today makes the default and
  // scrubbed states obey one rule: greeks = today, curve = selected date.
  // Greeks over time are still available in the payoff table's date × price grid.
  const displayMetrics = strategyMetrics;
  const scrubDateLabel = new Date(timeScrub.evalMs).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  // Normalized 0..1 position so the slider's right end maps exactly to expiration
  // (a 1-day step can't land on the precise expiration ms). The fill end is inset
  // by the 16px thumb radius so the knob tracks the end of the filled track.
  const scrubFraction = Math.max(
    0,
    Math.min(
      1,
      (timeScrub.evalMs - timeScrub.minMs) / Math.max(1, timeScrub.maxMs - timeScrub.minMs),
    ),
  );
  const scrubSliderValue = Math.round(scrubFraction * 1000);
  const scrubFillPos = `calc(${scrubFraction} * (100% - 16px) + 8px)`;

  // const strikePrices = strategyValues?.legs?.map(option => option.params.strike);

  const firstStock = stockOptions[0];
  const stockAtExpiration =
    // @ts-ignore
    chain?.[ticker]?.entries?.find((s) => !!firstStock && s.e === firstStock.expirationKey) ||
    chain?.[ticker as string]?.entries?.[DEMO_CHAIN_INDEX];

  const expirationDate = firstStock?.expirationDateString
    ? dateStringToMarketClose(firstStock.expirationDateString)
    : stockAtExpiration?.ey && stockAtExpiration?.em && stockAtExpiration?.ed
    ? expirationToDate(
        stockAtExpiration?.ey || 0,
        stockAtExpiration?.em || 0,
        stockAtExpiration?.ed || 0,
      )
    : undefined;

  const expirationKey = firstStock?.expirationKey || stockAtExpiration?.e || undefined;

  const getColorStyle = (profitPercent: number | null): React.CSSProperties => {
    if (profitPercent == null || !Number.isFinite(profitPercent)) {
      return { backgroundColor: 'rgba(0, 0, 0, 1)' };
    }

    const color = profitPercent >= 0 ? '0, 188, 55' : '168, 0, 35';
    const maxPercentValue = 100;
    const opacity = Math.max(0.05, Math.min(1, Math.abs(profitPercent) / maxPercentValue));

    return { backgroundColor: `rgba(${color}, ${opacity})` };
  };

  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  const openStrategyPanel = (slug: string) => {
    const filteredStrategies = allStrategies?.filter((strategy) => strategy.slug === slug);

    setStrategyData(filteredStrategies);
  };

  const showIcon = useCallback((slug: string) => {
    const iconData = allStrategies?.filter((strategy) => strategy.slug === slug);
    const marketConditions = iconData[0]?.market_conditions.split(', ');
    return marketConditions?.map((condition: string) => {
      const colorClasses: Record<any, string> = {
        Bearish: 'bg-opc-bearish text-white bg-opacity-75',
        Neutral: 'bg-opc-neutral text-text-neutral bg-opacity-90',
        Bullish: 'bg-opc-bullish text-text-bullish',
        Volatile: 'bg-opc-volatile text-text-volatile bg-opacity-90',
        Stable: 'bg-opc-stable text-text-stable bg-opacity-85',
        Income: 'bg-opc-income text-opc-text-income bg-opacity-85',
        Growth: 'bg-opc-growth text-opc-text-growth bg-opacity-85',
        'Very Bearish': 'bg-opc-very-bearish text-white bg-opacity-85',
        'Very Bullish': 'bg-opc-very-bullish text-white bg-opacity-85',
      };

      const colorClass = colorClasses[condition as any] || 'bg-gray-100 text-gray-800';

      return (
        <span
          key={`key-structureLines-${condition}`}
          className={`rounded-full p-1 ${colorClass} flex h-5 w-5 items-center justify-center`}
        >
          <Icon condition={condition} className="w-[16px]" />
        </span>
      );
    });
  }, []);

  const handleLoadStrategy = () => {
    setIsListStrategyModalOpen(true);
  };

  const handleStrategySelect = useCallback(
    (strategyData: SavedOpcStrategy) => {
      setStrategyName('Saved Strategy');

      const formattedPositions = strategyData?.positions?.map((position: any) => ({
        expirationDateString: position.expiration,
        expirationDate: dateStringToMarketClose(position.expiration),
        expirationKey: position.expiration,
        numberOfContracts: position.quantity,
        position: position.purchaseDirection,
        premium: position.premium,
        strike: position.strike,
        type: position.optionType,
      }));

      const sortedPositions = formattedPositions.sort(stockSortFunction);
      setStockOptions(sortedPositions || []);
      setUnderlyingStocks(
        strategyData?.stockPrice && strategyData?.stockShares
          ? [
              {
                price: strategyData.stockPrice,
                quantity: strategyData.stockShares,
              },
            ]
          : [],
      );
      setIsListStrategyModalOpen(false);
    },
    [setStockOptions, setIsListStrategyModalOpen],
  );

  const handleDeleteStrategy = async (strategy: SavedOpcStrategy) => {
    try {
      await archiveStrategyMutation({
        variables: {
          id: strategy.id,
          archivedAt: new Date().toISOString(),
        },
        refetchQueries: ['getOpcStrategiesForUser'],
        awaitRefetchQueries: true,
      });
    } catch (error) {
      console.error('Archiving failed:', error);
    }
  };

  const setCustomStrategyName = useCallback(() => {
    setStrategyName('Custom');
  }, [setStrategyName]);
  const clearStrategyName = useCallback(() => {
    setStrategyName('');
  }, [setStrategyName]);

  useEffect(() => {
    if (ticker && !tickerData?.length) {
      (async () => {
        try {
          const response = await getTickerList();
          setTickerData(response);
        } catch (error) {
          console.error('Failed to fetch data');
        }
      })();
    }
  }, [ticker, tickerData?.length]);

  const selectedTicker = useMemo(
    () => tickerData?.find((item) => item.t === ticker) || tickerData?.[0],
    [tickerData, ticker],
  );

  const matchingStrategy = allStrategies?.find((s) => s.slug === strategyType);
  const strategyDataForContent = strategyDefinition || matchingStrategy;

  const optionData = useMemo(() => {
    const currentPrice = spot;
    const strikePrice = firstStock?.strike || 0;
    const timeToExpiration = calculateTimeToExpiration(expirationDate || new Date());
    const data = payoffGraph || [];

    return {
      spot,
      currentPrice,
      strikePrice,
      timeToExpiration,
      riskFreeRate,
      volatility,
      priceRangePercentDecimal,
      data,
    };
  }, [spot, payoffGraph, firstStock?.strike, expirationDate, riskFreeRate, volatility]);

  const probabilityData = useMemo(() => {
    return optionData.volatility == null
      ? []
      : StrategyBuilder.generateProbabilityData({
          ...optionData,
          volatility: optionData.volatility,
        });
  }, [optionData]);

  /**
   * @todo Track profitability of strategies
   */

  return (
    <>
      <Head
        title={
          strategyDataForContent?.strategy
            ? `${strategyDataForContent?.strategy} ${TITLE_SUFFIX} | Visualize + Live Data`
            : 'OPC'
        }
        description={
          // matchingStrategy?.description ? matchingStrategy?.description?.Overview.join('. ') : ''
          generateMetaDescription(strategyDataForContent?.strategy)
        }
        url={getOpcStrategyPage(strategyType as string)}
        ogImage={content?.featuredImage ? urlFor(content?.featuredImage).url() : undefined}
      />
      {/* <div className="fixed left-0 top-0 z-50 w-full">
        <TopBanner showCloseIcon shouldShowOnScroll={false} />
      </div> */}
      <>
        <div className="block sm:hidden">
          <PageTitle
            Icon={PageIcons.Opc}
            title="Options Profit Calculator"
            isH2
            left={<MarketDataStatus className="ml-2" surface="research" flowAccess={flowAccess} />}
          />
        </div>
        {!isUserSession && <TopNav />}
        <div className="mx-auto w-full grow lg:flex">
          <main className="flex-1 pb-24 lg:pb-12 xl:flex">
            <div className="xl:flex-1">
              <div className="xl:flex-1">
                <div className="mx-auto w-full">
                  <div className="items-center justify-center">
                    <Popover className="relative isolate z-10 shadow">
                      <div className="mt-2 flex flex-col px-2 md:flex-row">
                        <div className="w-full rounded-md border border-graph-green bg-palette-black-4 p-4 leading-none shadow-lg transition-colors duration-200 ease-in-out md:w-1/3">
                          <div className="relative flex">
                            <Combobox
                              as="div"
                              value={ticker}
                              onChange={(newTicker) => {
                                if (newTicker !== ticker) {
                                  clearStrategyName();
                                  router.push(
                                    getOpcStrategyPage(strategyType as string, newTicker),
                                  );
                                }
                              }}
                              className="w-full"
                            >
                              <div className="flex items-center gap-2">
                                <Combobox.Input
                                  className="w-full rounded-lg border border-palette-border bg-palette-black-2 p-4 py-2 text-sm font-semibold text-white outline-none ring-palette-border focus:ring-brand-primary"
                                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                    search(event.target.value)
                                  }
                                  placeholder="Enter ticker (ex. TSLA)"
                                  autoComplete={'off'}
                                  displayValue={(item: TickerType) => item?.t || (ticker as string)}
                                />
                                <div className="flex items-center justify-start gap-2">
                                  {showIcon(strategyType as string)}
                                </div>
                              </div>
                              {searchResults?.length > 0 && (
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto border border-palette-border bg-palette-black-1 py-1 text-base  shadow-lg focus:outline-none sm:text-sm">
                                  {searchResults?.map((ticker, index) => (
                                    <Combobox.Option
                                      key={ticker?.t || index}
                                      value={ticker?.t}
                                      className={({ active }) =>
                                        classNames(
                                          'relative mb-0 cursor-default select-none px-2 py-1',
                                          active ? 'bg-brand-primary  text-white' : 'text-white',
                                        )
                                      }
                                    >
                                      {({ selected }) => (
                                        <div className="flex items-center truncate">
                                          <span
                                            className={classNames(
                                              'mr-1',
                                              selected ? 'font-bold' : 'font-semibold',
                                            )}
                                          >
                                            <span className="block w-[44px]">{ticker.t}</span>
                                          </span>
                                          <span
                                            className={classNames(
                                              'block truncate',
                                              selected
                                                ? 'font-semibold text-white'
                                                : 'text-gray-300',
                                            )}
                                          >
                                            {ticker.n}
                                          </span>
                                        </div>
                                      )}
                                    </Combobox.Option>
                                  ))}
                                </Combobox.Options>
                              )}
                            </Combobox>
                          </div>
                          <div className="mt-2 flex">
                            <div className="flex w-full justify-end">
                              <div
                                className="isolate inline-flex w-full"
                                onClick={() => setIsOpen(true)}
                              >
                                <button
                                  type="button"
                                  className="relative flex w-full items-center justify-between gap-x-1.5 rounded-md bg-brand-primary px-4 py-1 text-sm font-semibold text-white outline-none focus:z-10"
                                >
                                  {strategyName || formatStrategyName(strategyType as string)}
                                  <span className="relative inline-flex items-center bg-brand-primary py-2 pl-3 text-sm font-semibold text-white  outline-none focus:z-10">
                                    {isOpen ? (
                                      <FaChevronUp className="h-3 w-3 text-white" />
                                    ) : (
                                      <FaChevronDown
                                        className="h-3 w-3 text-white"
                                        aria-hidden="true"
                                      />
                                    )}
                                  </span>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                        <TickerDetailsCard
                          activeTicker={ticker}
                          sector={''}
                          refreshTrigger={refreshTrigger}
                        />
                        <div className="mt-2 flex w-full items-center rounded-md bg-palette-black-4 leading-none shadow-lg transition-colors duration-200 ease-in-out md:ml-2 md:mt-0 md:w-1/3">
                          <GreekLetters
                            strategyMetrics={displayMetrics}
                            volatility={volatility}
                            isManual={isManualVolatility}
                          />
                        </div>
                      </div>
                    </Popover>
                  </div>
                </div>
                {!isBusy &&
                  stockOptions.length > 0 &&
                  (isManualVolatility ||
                    ivSummary.impliedVolatilities.some(
                      (result) => result.status === 'unavailable',
                    )) && (
                    <VolatilityControl
                      results={ivSummary.impliedVolatilities}
                      volatility={volatility}
                      isManual={isManualVolatility}
                      onEstimate={setVolatility}
                      onReset={resetVolatility}
                    />
                  )}
                {!isBusy && results && <PricingEngineStatus pricing={results.pricing} />}
                {isBusy ? (
                  <StrategyStatsSkeleton />
                ) : !results ? null : (
                  <div className="mt-2 px-2">
                    <div className="mx-auto max-w-6xl rounded-md bg-palette-black-4 px-2 py-2.5">
                      <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
                        <div className="space-y-1 px-1 text-center">
                          <p className="text-center text-xs font-medium">
                            {!!strategyMetrics?.netDebit && strategyMetrics.netDebit >= 0
                              ? 'Premium Received'
                              : 'Premium Paid'}
                          </p>
                          {!!strategyMetrics?.netDebit && (
                            <p className="flex items-center justify-center gap-x-2">
                              <FaSackDollar
                                className={classNames(
                                  'h-3 w-3',
                                  'text-yellow-300',
                                  // strategyMetrics?.netDebit >= 0 ? 'text-green-300' : 'text-red-300',
                                )}
                              />
                              <span
                                className={classNames(
                                  'text-xs font-semibold tracking-tight',
                                  // strategyMetrics?.netDebit >= 0 ? 'text-green-200' : 'text-red-200',
                                )}
                              >
                                {isFinite(strategyMetrics?.netDebit)
                                  ? `$${new Intl.NumberFormat('en-US').format(
                                      Math.abs(parseInt(strategyMetrics?.netDebit.toFixed(0), 10)),
                                    )}`
                                  : strategyMetrics?.netDebit?.toFixed(0)}
                              </span>
                            </p>
                          )}
                        </div>
                        <div className="space-y-1 px-1">
                          <p className="text-center text-xs font-medium">Max Profit</p>
                          {!!maxProfit && (
                            <p className="flex items-center justify-center gap-x-2">
                              <span>
                                <BsGraphUpArrow className="h-3 w-3 text-green-500" />
                              </span>
                              <span className="text-xs font-semibold tracking-tight text-white">
                                {isFinite(maxProfit)
                                  ? `$${new Intl.NumberFormat('en-US').format(
                                      parseInt(maxProfit.toFixed(0), 10),
                                    )}`
                                  : maxProfit?.toFixed(0)}
                              </span>
                            </p>
                          )}
                        </div>
                        <div className="space-y-1 px-1">
                          <p className="text-center text-xs font-medium">Max Loss</p>
                          {!!maxLoss && (
                            <p className="flex items-center justify-center gap-x-2">
                              <span>
                                <BsGraphDownArrow className="h-3 w-3 text-red-600" />
                              </span>
                              <span className="text-xs font-semibold tracking-tight text-white">
                                {isFinite(maxLoss)
                                  ? `$${new Intl.NumberFormat('en-US').format(
                                      Math.abs(parseInt(maxLoss.toFixed(0), 10)),
                                    )}`
                                  : maxLoss?.toFixed(0)}
                              </span>
                            </p>
                          )}
                        </div>
                        <div className="space-y-1 px-1">
                          <p className="text-center text-xs font-medium">Profit Index</p>
                          {normalizedProfitFactor !== undefined &&
                            !isNaN(normalizedProfitFactor) && (
                              <p className="flex items-center justify-center gap-x-2">
                                <span>
                                  <LuScale3D className="h-4 w-4 text-electric-teal" />
                                </span>
                                <span className="text-xs font-semibold tracking-tight text-white">
                                  {`${roundToDecimals(Math.abs(normalizedProfitFactor), 2)}`}
                                </span>
                              </p>
                            )}
                        </div>
                        <div className="space-y-1 px-1">
                          <p className="text-center text-xs font-medium">Probability of Profit</p>
                          {probabilityOfProfit !== undefined && (
                            <p className="flex items-center justify-center gap-x-2">
                              <span>
                                <TbZoomMoney className="h-4 w-4 text-top-position" />
                              </span>
                              <span className="text-xs font-semibold tracking-tight text-white">
                                {`${roundToDecimals(probabilityOfProfit * 100, 2)}%`}
                              </span>
                            </p>
                          )}
                        </div>
                        <div className="space-y-1 px-1">
                          <p className="text-center text-xs font-medium">Break Even Prices</p>
                          <p className="flex items-center justify-center gap-x-2">
                            {!!breakEvenPoints && (
                              <span>
                                <FiActivity className="h-4 w-4 text-graph-purple" />
                              </span>
                            )}
                            <span className="text-xs font-semibold tracking-tight text-white">
                              {hasErraticBehavior
                                ? '-'
                                : breakEvenPoints
                                    ?.map((bep) => roundToDecimals(bep, 2))
                                    ?.join(', ')}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {isBusy ? (
                  <StrategyChartTableSkeleton />
                ) : !results ? (
                  <p className="p-6 text-center text-sm" role="status">
                    Valuation unavailable. Review the option inputs or choose a manual volatility
                    estimate above.
                  </p>
                ) : hasErraticBehavior ? (
                  <div className="flex min-h-[24rem] flex-col items-center justify-center px-2 text-center">
                    <div className="styled-font text-3xl font-bold">Invalid strategy</div>
                    <p className="mx-auto mt-4 max-w-xl text-palette-black-8">
                      The configuration of your strategy will never generate a profit or loss. It is
                      likely that your positions offset. A common case is buying and selling the
                      exact same option, such as going long and short on a with the same ticker,
                      strike, expiration, and premium.
                    </p>
                  </div>
                ) : (
                  <div className="mt-4 px-2">
                    <div>
                      <div className="">
                        <nav className="isolate rounded-md lg:flex" aria-label="Tabs">
                          {Object.entries(TableSetting).map(([key, value], i) => {
                            const displayName = tableSettingDisplayNames[value];
                            return (
                              <button
                                key={key}
                                onClick={() => setTableSetting(value)}
                                className={classNames(
                                  tableSetting === value ? 'bg-brand-primary' : '',
                                  i === 0 && 'lg:rounded-l-md',
                                  i === Object.entries(TableSetting).length - 1 && 'rounded-r-md',
                                  'group relative inline-flex w-full min-w-0 cursor-pointer justify-center overflow-hidden border border-brand-primary px-2 py-2 text-center text-xs  font-medium text-gray-100 lg:flex-1',
                                )}
                              >
                                <span>{displayName}</span>
                              </button>
                            );
                          })}
                        </nav>
                      </div>
                    </div>
                    {!isGreekTableSetting(tableSetting) && (
                      <div className="mt-4">
                        <PayoffChart
                          data={chartData}
                          breakEvenPoints={chartBreakEvens}
                          activeItem={tableSettingToKeyName(tableSetting)}
                          currentStockPrice={spot}
                          xMin={spot * (1 - priceRangePercentDecimal)}
                          xMax={spot * (1 + priceRangePercentDecimal)}
                          maxLoss={maxLoss}
                          // optionData={optionData}
                          probabilityData={probabilityData}
                          expirationData={payoffGraph || []}
                          showExpirationReference={
                            !timeScrub.isDefault && !timeScrub.isAtExpiration
                          }
                        />
                        {canScrubTime && (
                          <div className="mt-3">
                            <div className="mb-1 flex items-center justify-between">
                              <label
                                htmlFor="time-scrub"
                                className="text-xs font-semibold text-white dark:text-white"
                              >
                                Sell date:{' '}
                                {timeScrub.isDefault || timeScrub.isAtExpiration
                                  ? 'At expiration'
                                  : `${scrubDateLabel} · ${timeScrub.daysToExpiration}d to exp`}
                              </label>
                              <div className="flex items-center gap-2">
                                {!timeScrub.isDefault && timeScrub.livePoint && (
                                  <span
                                    className={`text-xs font-semibold ${
                                      timeScrub.livePoint.profit >= 0
                                        ? 'text-green-400'
                                        : 'text-red-400'
                                    }`}
                                  >
                                    P/L at current spot (${spot}):{' '}
                                    {timeScrub.livePoint.profit >= 0 ? '+' : '-'}$
                                    {Math.abs(timeScrub.livePoint.profit).toFixed(0)}
                                  </span>
                                )}
                                {!timeScrub.isDefault && (
                                  <button
                                    type="button"
                                    onClick={timeScrub.reset}
                                    className="text-palette-blue-2 text-xs font-medium hover:underline"
                                  >
                                    Reset
                                  </button>
                                )}
                              </div>
                            </div>
                            <input
                              id="time-scrub"
                              type="range"
                              min={0}
                              max={1000}
                              step={1}
                              value={scrubSliderValue}
                              className="opc-range h-2 w-full cursor-pointer rounded-lg bg-transparent"
                              style={{
                                background: `linear-gradient(90deg, #3C8DF7 0%, #3C8DF7 ${scrubFillPos}, #4B5563 ${scrubFillPos}, #4B5563 100%)`,
                              }}
                              onChange={(e) =>
                                timeScrub.setEvalMs(
                                  timeScrub.minMs +
                                    (parseInt(e.target.value, 10) / 1000) *
                                      (timeScrub.maxMs - timeScrub.minMs),
                                )
                              }
                            />
                            <div className="mt-0.5 flex justify-between text-[10px] text-gray-400">
                              <span>Today</span>
                              <span>Expiration</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-4 py-4">
                      <div>
                        <label className="mb-1 block text-xs font-semibold text-white dark:text-white">
                          Price Range: {displayPriceRange(priceRangePercentDecimal).toFixed(1)}%
                        </label>
                        <input
                          id="price-range"
                          type="range"
                          min="1"
                          max={displayPriceRange(priceRangeMaxPercentDecimal)}
                          step="0.1"
                          value={displayPriceRange(priceRangePercentDecimal).toFixed(1)}
                          className="inset-0 h-2 w-full cursor-pointer appearance-none rounded-lg bg-transparent"
                          style={{
                            background: `linear-gradient(90deg, #3C8DF7 0%, #3C8DF7 ${
                              (priceRangePercentDecimal / priceRangeMaxPercentDecimal) * 100
                            }%, #4B5563 ${
                              (priceRangePercentDecimal / priceRangeMaxPercentDecimal) * 100
                            }%, #4B5563 ${
                              (1 - priceRangePercentDecimal / priceRangeMaxPercentDecimal) * 100
                            }%)`,
                          }}
                          onChange={(e) => {
                            const newPriceRange = storedPriceRange(parseFloat(e.target.value));
                            setPriceRangePercentDecimal(newPriceRange);
                          }}
                        />
                        {/* <div
                          className="absolute top-0 hidden -translate-y-full text-xs"
                          style={{
                            left: `${
                              displayPriceRange(priceRangePercentDecimal) /
                              displayPriceRange(priceRangeMaxPercentDecimal)
                            }%`,
                            transform: 'translateX(-50%)',
                          }}
                        >
                          {displayPriceRange(priceRangePercentDecimal).toFixed(1)}%
                        </div> */}
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-semibold text-white dark:text-white">
                          {isManualVolatility
                            ? 'Manual / estimated volatility'
                            : 'Implied Volatility'}
                          :{' '}
                          {volatility == null
                            ? 'Unavailable'
                            : `${displayVolatility(volatility).toFixed(1)}%`}
                        </label>
                        <input
                          id="volatility-range"
                          type="range"
                          min="0.1"
                          max={volatilitySlider.maxPercent}
                          step="0.1"
                          value={volatilitySlider.valuePercent.toFixed(1)}
                          aria-describedby="volatility-range-help"
                          className="inset-0 h-2 w-full cursor-pointer appearance-none rounded-lg bg-transparent"
                          style={{
                            background: `linear-gradient(90deg, #3C8DF7 0%, #3C8DF7 ${volatilitySlider.fillPercent}%, #4B5563 ${volatilitySlider.fillPercent}%, #4B5563 100%)`,
                          }}
                          onChange={(e) => {
                            const newVolatility = storedVolatility(parseFloat(e.target.value));
                            setVolatility(newVolatility);
                          }}
                        />
                        {originalStrategyVolatility != null && (
                          <div className="relative h-4">
                            {[1, 2].map((multiple) => (
                              <span
                                key={multiple}
                                className="absolute inline-flex -translate-x-1/2 items-center text-xs text-white"
                                style={{
                                  left: `${
                                    ((displayVolatility(originalStrategyVolatility) * multiple) /
                                      volatilitySlider.maxPercent) *
                                    100
                                  }%`,
                                }}
                              >
                                <span className="text-palette-black-7">
                                  {(
                                    displayVolatility(originalStrategyVolatility) * multiple
                                  ).toFixed(1)}
                                  %
                                </span>
                                <span className="ml-1 font-bold">{multiple}x</span>
                              </span>
                            ))}
                          </div>
                        )}
                        <p id="volatility-range-help" className="sr-only">
                          Moving this slider selects a manual volatility estimate; it does not
                          re-solve implied volatility from quotes.
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 overflow-x-auto py-4">
                      <table className="min-w-full">
                        <thead>
                          <tr>
                            <th className="rounded-tl-md bg-palette-black-3 p-2 text-xs font-medium text-white">
                              {tableSettingShortDisplayNames[tableSetting]}
                            </th>
                            {payoffTable?.[0].map((entry, index) => (
                              <th
                                key={index}
                                className={classNames(
                                  'bg-palette-black-4 text-xs font-medium uppercase text-white',
                                  index === payoffTable[0].length - 1 && 'rounded-tr-md',
                                )}
                              >
                                {formatDate(entry.timestamp)}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {payoffTable?.map((row, rowIndex) => {
                            return (
                              <tr
                                key={rowIndex}
                                className={classNames('bg-palette-black-3 text-xs text-white')}
                              >
                                <td
                                  className={classNames(
                                    'flex w-16 p-1',
                                    rowIndex === payoffTable.length - 1 && 'rounded-bl-md',
                                  )}
                                >
                                  <span className="flex-1">${row[0].stockPrice}</span>
                                  <span className="px-2 text-[11px] text-gray-500">
                                    {row[0].stockPriceRelativeToSpot}
                                  </span>
                                </td>
                                {row.map((cell, cellIndex) => {
                                  const display = getTableValue(tableSetting, cell, maxLoss);
                                  const cellStyle = getColorStyle(
                                    display.title ? null : cell.profitPercent,
                                  );

                                  return (
                                    <td
                                      key={cellIndex}
                                      style={cellStyle}
                                      className={classNames(
                                        'text-center',
                                        rowIndex === payoffTable.length - 1 &&
                                          cellIndex === row.length - 1 &&
                                          'rounded-br-md',
                                      )}
                                    >
                                      <TableValue display={display} />
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
              {content?.body && (
                <div className="mt-12 border-t border-palette-border pb-12 pt-12">
                  <div className="prose prose-invert mx-auto max-w-4xl px-4">
                    <h1 className="styled-font">
                      {strategyDataForContent?.strategy} {TITLE_SUFFIX}
                    </h1>
                    {(content as any).editorialBody ? (
                      <EditorialBody
                        body={(content as any).editorialBody}
                        baseUrl={(content as any).contentBaseUrl}
                      />
                    ) : (
                      <PortableTextComponent value={content.body} />
                    )}
                  </div>
                  {content.faqs && content.faqs.length > 0 && (
                    <div className="prose prose-invert mx-auto mt-20 max-w-4xl px-4">
                      <h2 className="styled-font">{strategyDataForContent?.strategy} FAQs</h2>
                      <div className="space-y-10">
                        {content.faqs.map((faq) => {
                          if (!faq.answer) {
                            return null;
                          }

                          return (
                            <div key={faq.question}>
                              <h3>{faq.question?.replace('Q: ', '')}</h3>
                              {(faq as any).editorialBody ? (
                                <EditorialBody
                                  body={(faq as any).editorialBody}
                                  baseUrl={(faq as any).contentBaseUrl}
                                />
                              ) : (
                                <PortableTextComponent value={faq.answer} />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </main>
          <aside
            className="sticky top-0 hidden h-screen shrink-0 grid-rows-2 overflow-y-hidden border-palette-border p-2 lg:grid lg:w-[26.8rem] lg:border-l"
            style={{ height: 'calc(100vh - var(--member-banner-height, 0px))' }}
          >
            <div
              className={classNames(
                'flex flex-col overflow-hidden',
                getSinglePaneClass('top', splitLayout.expandedPane),
              )}
            >
              <div>
                <div className="items-center">
                  <div className="flex w-full items-center">
                    <div className="flex items-center">
                      <LayoutToggleButton
                        className="mr-2"
                        isExpanded={splitLayout.isExpanded('top')}
                        onClick={handleTogglePositionsPane}
                      />
                      <h2 className="mr-2 text-sm font-semibold">Positions</h2>
                      <PositionsRefreshControl
                        hasPaidAccess={hasPaidAccess}
                        isUserSession={isUserSession}
                        isBusy={isBusy || isFakeSpinning}
                        isLiveUpdatesEnabled={isLiveUpdatesEnabled}
                        onToggleLiveUpdates={setIsLiveUpdatesEnabled}
                        onRefresh={() => refreshData(ticker)}
                        onLoginRequired={() => setShowAuthModal(true)}
                      />
                    </div>
                    <div className="ml-auto flex">
                      <button
                        className="mr-1.5 rounded-md bg-yellow-400 px-2.5 py-1 text-sm font-bold text-black no-underline shadow-md disabled:bg-gray-400"
                        onClick={() => setIsSaveModalOpen(true)}
                        disabled={isBusy || !results}
                        title={!results ? 'Choose a usable volatility before saving.' : undefined}
                      >
                        Save
                      </button>
                      <button
                        className="rounded-md bg-yellow-400 px-2.5 py-1 text-sm font-bold leading-none text-black  shadow-md disabled:bg-gray-400"
                        onClick={() => handleLoadStrategy()}
                        disabled={isBusy}
                      >
                        Load
                      </button>
                    </div>
                  </div>
                  <div className="isolate mt-2 flex justify-center">
                    <button
                      type="button"
                      title="Buy Call"
                      className="flex w-full items-center justify-center rounded-l-md bg-brand-primary px-2.5 py-2 text-sm  font-medium ring-1 ring-inset ring-palette-black-1"
                      onClick={() => {
                        setCustomStrategyName();
                        insertStockOption(
                          convertStrategyParamsToStockOptions(
                            longCall({
                              chain: chain?.[ticker]?.entries,
                              spot: spot,
                              expirationDate: expirationDate,
                              expirationKey: expirationKey,
                            }).optionPositions.sort(strategyBuilderSortFunction) || [],
                          ),
                        );
                      }}
                    >
                      Call <FaArrowUp className="ms-2 h-3.5" />
                    </button>
                    <button
                      type="button"
                      title="Sell Call"
                      className="flex w-full items-center justify-center bg-brand-primary px-2.5 py-2 text-sm  font-medium ring-1 ring-inset ring-palette-black-1"
                      onClick={() => {
                        setCustomStrategyName();
                        insertStockOption(
                          convertStrategyParamsToStockOptions(
                            shortCall({
                              chain: chain?.[ticker]?.entries,
                              spot: spot,
                              expirationDate: expirationDate,
                              expirationKey: expirationKey,
                            }).optionPositions.sort(strategyBuilderSortFunction) || [],
                          ),
                        );
                      }}
                    >
                      Call <FaArrowDown className="ms-2 h-3.5" />
                    </button>
                    <button
                      type="button"
                      title="Buy Put"
                      className="flex w-full items-center justify-center bg-brand-primary px-2.5 py-2 text-sm  font-medium ring-1 ring-inset ring-palette-black-1"
                      onClick={() => {
                        setCustomStrategyName();
                        insertStockOption(
                          convertStrategyParamsToStockOptions(
                            longPut({
                              chain: chain?.[ticker]?.entries,
                              spot: spot,
                              expirationDate: expirationDate,
                              expirationKey: expirationKey,
                            }).optionPositions.sort(strategyBuilderSortFunction) || [],
                          ),
                        );
                      }}
                    >
                      Put <FaArrowUp className="ms-2 h-3.5" />
                    </button>
                    <button
                      type="button"
                      title="Sell Put"
                      className="flex w-full items-center justify-center bg-brand-primary px-2.5 py-2 text-sm  font-medium ring-1 ring-inset ring-palette-black-1"
                      onClick={() => {
                        setCustomStrategyName();
                        insertStockOption(
                          convertStrategyParamsToStockOptions(
                            shortPut({
                              chain: chain?.[ticker]?.entries,
                              spot: spot,
                              expirationDate: expirationDate,
                              expirationKey: expirationKey,
                            }).optionPositions.sort(strategyBuilderSortFunction) || [],
                          ),
                        );
                      }}
                    >
                      Put <FaArrowDown className="ms-2 h-3.5" />
                    </button>
                    <button
                      type="button"
                      title="Add Underlying Stock"
                      className="flex w-full items-center justify-center rounded-r-md bg-brand-primary px-4 py-2 text-sm  font-medium ring-1 ring-inset ring-palette-black-1"
                      onClick={() => {
                        setCustomStrategyName();
                        insertUnderlyingStock([
                          {
                            price: spot,
                            quantity: UNDERLYING_STOCK_POSITION_STEPPER,
                          },
                        ]);
                      }}
                    >
                      Stock
                    </button>
                  </div>
                </div>
              </div>
              <ul role="list" className="flex flex-auto flex-col overflow-y-auto py-2">
                {isBusy
                  ? Array(3)
                      .fill(0)
                      .map((_, index) => (
                        <li
                          key={`skeleton-${index}`}
                          className="mb-2 animate-pulse rounded-md border border-palette-border bg-palette-black-4 p-3"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="h-4 w-6 rounded bg-palette-black-2"></div>
                              <div className="h-4 w-12 rounded bg-palette-black-2"></div>
                              <div className="h-4 w-8 rounded bg-palette-black-2"></div>
                              <div className="h-4 w-16 rounded bg-palette-black-2"></div>
                              <div className="h-4 w-20 rounded bg-palette-black-2"></div>
                            </div>
                            <div className="h-7 w-7 rounded-md bg-palette-black-2"></div>
                          </div>
                        </li>
                      ))
                  : stockOptions?.map((option, index) => {
                      const highlightClass =
                        index === lastAddedIndex ? '!border-graph-green !border' : '';
                      const isLong = option.numberOfContracts >= 0;
                      const strikeDirection = isLong ? 'ask' : 'bid';
                      const isCallOption = option.type === CALL;

                      return (
                        <React.Fragment key={`option-${index}`}>
                          {isEditingOption ? (
                            <li
                              className={classNames(
                                highlightClass,
                                'rounded-md bg-palette-black-4 p-3 text-xs',
                              )}
                            >
                              <div className="grid grid-cols-[0.6fr_1fr_1.4fr] gap-2">
                                <div className="rounded-md bg-palette-black-4">
                                  <label className="leadin-none mb-1 block font-semibold">
                                    Strike price
                                  </label>
                                  <div className="">
                                    <StrikeCombobox
                                      value={option.strike}
                                      onChange={(strike) => {
                                        setCustomStrategyName();
                                        const stockAtIndex = stockOptions[index];

                                        const premiumsAtStrike = chain?.[ticker]?.entries?.find(
                                          (entry) => entry.e === stockAtIndex.expirationKey,
                                        )?.c?.[strike];
                                        const premiumsForType =
                                          stockAtIndex.type === CALL
                                            ? premiumsAtStrike?.c
                                            : premiumsAtStrike?.p;

                                        const premium = premiumsForType
                                          ? getPremium({
                                              ask: premiumsForType?.a!,
                                              bid: premiumsForType?.b!,
                                              strikeDirection,
                                            })
                                          : parseFloat(
                                              calculatePremium({
                                                spot: spot,
                                                strike: strike,
                                                timeToExpirationInYears:
                                                  calculateTimeToExpirationInYears(
                                                    stockAtIndex.expirationDateString,
                                                    new Date(),
                                                  ),
                                                riskFreeRate: riskFreeRate,
                                                type: stockAtIndex.type,
                                                isCall: stockAtIndex.type === CALL,
                                                isPut: stockAtIndex.type === PUT,
                                                isLong: stockAtIndex.numberOfContracts >= 0,
                                                isShort: stockAtIndex.numberOfContracts < 0,
                                                volatility: volatility || 0.15, // It's not used in the calculation so picking random
                                                position:
                                                  stockAtIndex.numberOfContracts >= 0
                                                    ? 'long'
                                                    : 'short',
                                              }).toFixed(2),
                                            );

                                        updateStockOptionAtIndex(index, {
                                          ...option,
                                          strike: strike,
                                          premium: premium || stockAtIndex.premium,
                                        });
                                      }}
                                      chain={chain}
                                      ticker={ticker}
                                      expirationKey={option.expirationKey}
                                      optionType={option.type as 'CALL' | 'PUT'}
                                      spot={spot}
                                    />
                                  </div>
                                </div>
                                <div className="">
                                  <div className="gap-x-3">
                                    <label
                                      className="leadin-none mb-1 block font-semibold"
                                      htmlFor="optionType"
                                    >
                                      Type
                                    </label>
                                    <div>
                                      <select
                                        className="flex w-full items-center justify-between rounded border border-palette-border bg-palette-black-1 py-1 pl-2 pr-2"
                                        id="optionType"
                                        name="optionType"
                                        value={option.type}
                                        onChange={(e) => {
                                          setCustomStrategyName();

                                          const newOptionType = e.target.value as OptionType;

                                          // Get available strikes for the new option type
                                          const availableStrikes = getAvailableStrikes(
                                            chain,
                                            ticker,
                                            option.expirationKey,
                                            newOptionType as 'CALL' | 'PUT',
                                          );

                                          // Check if current strike is available in new option type
                                          let newStrike = option.strike;
                                          if (
                                            availableStrikes.length > 0 &&
                                            !availableStrikes.includes(option.strike)
                                          ) {
                                            // Find closest strike to current strike or spot
                                            const targetPrice = option.strike || spot;
                                            newStrike = availableStrikes.reduce((prev, curr) =>
                                              Math.abs(curr - targetPrice) <
                                              Math.abs(prev - targetPrice)
                                                ? curr
                                                : prev,
                                            );
                                          }

                                          const premiums = chain?.[
                                            selectedTicker?.t || DEFAULT_TICKER
                                          ]?.entries?.find(
                                            (entry) => entry.e === option.expirationKey,
                                          )?.c?.[newStrike];
                                          const isCall = newOptionType === CALL;
                                          const premiumsForType = isCall
                                            ? premiums?.c
                                            : premiums?.p;
                                          const premium = getPremium({
                                            ask: premiumsForType?.a!,
                                            bid: premiumsForType?.b!,
                                            strikeDirection,
                                          });

                                          updateStockOptionAtIndex(index, {
                                            ...option,
                                            type: newOptionType,
                                            strike: newStrike,
                                            premium,
                                          });
                                        }}
                                      >
                                        <option value={CALL}>Call</option>
                                        <option value={PUT}>Put</option>
                                      </select>
                                    </div>
                                  </div>
                                </div>
                                <div className="">
                                  <div className="items-center gap-x-3">
                                    <label className="leadin-none mb-1 block font-semibold">
                                      Expiration
                                    </label>
                                    <div>
                                      <DateDropdown
                                        chain={chain as OptionChain}
                                        ticker={ticker}
                                        defaultValue={option.expirationKey}
                                        value={option.expirationKey}
                                        setValue={(entry) => {
                                          setCustomStrategyName();

                                          // Get available strikes for the new expiration
                                          const availableStrikes = getAvailableStrikes(
                                            chain,
                                            ticker,
                                            entry.e!,
                                            option.type as 'CALL' | 'PUT',
                                          );

                                          // Check if current strike is available in new expiration
                                          let newStrike = option.strike;
                                          if (
                                            availableStrikes.length > 0 &&
                                            !availableStrikes.includes(option.strike)
                                          ) {
                                            // Find closest strike to current strike or spot
                                            const targetPrice = option.strike || spot;
                                            newStrike = availableStrikes.reduce((prev, curr) =>
                                              Math.abs(curr - targetPrice) <
                                              Math.abs(prev - targetPrice)
                                                ? curr
                                                : prev,
                                            );
                                          }

                                          const premiums = entry?.c?.[newStrike];
                                          const isCall = option.type === CALL;
                                          const premiumsForType = isCall
                                            ? premiums?.c
                                            : premiums?.p;
                                          const premium = getPremium({
                                            ask: premiumsForType?.a!,
                                            bid: premiumsForType?.b!,
                                            strikeDirection,
                                          });

                                          updateStockOptionAtIndex(index, {
                                            ...option,
                                            strike: newStrike,
                                            premium,
                                            expirationKey: entry.e!,
                                            expirationDateString: dateToDateString(
                                              expirationToDate(entry.ey!, entry.em!, entry.ed!),
                                            ),
                                          });
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                                <div className="">
                                  <div className="items-center gap-x-3">
                                    <label className="leadin-none mb-1 block font-semibold">
                                      Quantity
                                    </label>
                                    <div>
                                      <input
                                        className="w-full rounded border border-palette-border bg-palette-black-1 px-2 py-1"
                                        id="numberOfContracts"
                                        type="number"
                                        value={option.numberOfContracts}
                                        onChange={(e) => {
                                          setCustomStrategyName();

                                          updateStockOptionAtIndex(index, {
                                            ...option,
                                            numberOfContracts: parseFloat(e.target.value || '0'),
                                          });
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                                <div className="">
                                  <div className="items-center gap-x-3">
                                    <label className="leadin-none mb-1 block font-semibold">
                                      Premium
                                    </label>
                                    <div>
                                      <input
                                        className="w-full rounded border border-palette-border bg-palette-black-1 px-2 py-1"
                                        id="premium"
                                        type="number"
                                        value={option.premium === 0.01 ? '' : option.premium}
                                        onChange={(e) => {
                                          setCustomStrategyName();

                                          const premiumValue = e.target.value;

                                          // If field is empty, use minimal premium (0.01) as default
                                          let premium: number;
                                          if (!premiumValue || premiumValue.trim() === '') {
                                            premium = 0.01;
                                          } else {
                                            const parsedPremium = parseFloat(premiumValue);
                                            // Skip update if parseFloat results in NaN or negative number
                                            if (isNaN(parsedPremium) || parsedPremium < 0) {
                                              return;
                                            }
                                            premium = parsedPremium;
                                          }

                                          updateStockOptionAtIndex(index, {
                                            ...option,
                                            premium: premium,
                                          });
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="mt-1.5 flex w-full justify-between gap-3 pr-1">
                                <button
                                  className="flex items-center font-medium leading-none text-green-300 focus:outline-none"
                                  onClick={() => {
                                    setIsEditingOption(false);
                                    sortStockOptions();
                                  }}
                                  title="Save"
                                >
                                  <span className="mr-1 text-lg leading-none">
                                    <IoCheckmark className="h-4" />
                                  </span>
                                  Done
                                </button>
                                <div className="flex shrink-0 items-center gap-3">
                                  <button
                                    className="flex items-center font-medium leading-none focus:outline-none"
                                    onClick={() => {
                                      setCustomStrategyName();

                                      insertStockOption([{ ...option }]);
                                    }}
                                    title="Duplicate Option"
                                  >
                                    <span className="mr-1 text-lg leading-none">
                                      <IoCopy className="h-3" />
                                    </span>
                                    Duplicate
                                  </button>
                                  <button
                                    className="flex items-center font-medium leading-none text-red-400 hover:text-red-500 focus:outline-none"
                                    onClick={() => {
                                      setCustomStrategyName();

                                      removeStockOptionAtIndex(index);
                                    }}
                                    title="Delete Option"
                                  >
                                    <span className="mr-1 text-lg leading-none">&times;</span>
                                    Delete
                                  </button>
                                </div>
                              </div>
                            </li>
                          ) : (
                            <li
                              className={classNames(
                                'flex items-center justify-between rounded-md border py-2 pl-3 pr-2 text-sm',
                                isCallOption
                                  ? 'border-graph-green bg-green-950/20'
                                  : 'border-graph-red bg-red-950/20',
                              )}
                            >
                              <div className="flex items-center">
                                <span className="mr-3 flex items-center">
                                  {option.numberOfContracts > 0 && (
                                    <span>
                                      <ArrowUpIcon className="w-3" />
                                    </span>
                                  )}
                                  {option.numberOfContracts < 0 && (
                                    <span>
                                      <ArrowDownIcon className="w-3" />
                                    </span>
                                  )}
                                  {option.numberOfContracts === 0 && <span>-</span>}
                                  {option.numberOfContracts}
                                </span>
                                <span className="font-semibold">{option.strike}</span>
                                <span
                                  className={classNames(
                                    'ml-0.5 font-normal text-palette-black-7',
                                    isCallOption ? 'text-graph-green' : 'text-graph-red',
                                  )}
                                >
                                  {isCallOption ? 'C' : 'P'}
                                </span>
                                <span className="ml-3 text-xs">
                                  <span className="text-palette-black-7">@ </span>$
                                  {option.premium.toFixed(2)}
                                </span>
                                <span className="ml-3 flex items-center text-xs">
                                  <CalendarIcon className="mr-1 w-3 text-palette-black-7" />
                                  {option.expirationKey}
                                </span>
                              </div>
                              <button
                                className="flex h-7 w-7 items-center justify-center rounded-md bg-brand-primary text-xs"
                                onClick={() => setIsEditingOption(true)}
                              >
                                <PencilIcon className="w-3.5" />
                              </button>
                            </li>
                          )}
                        </React.Fragment>
                      );
                    })}
                {isBusy
                  ? underlyingStocks && underlyingStocks.length > 0
                    ? Array(1)
                        .fill(0)
                        .map((_, index) => (
                          <li
                            key={`stock-skeleton-${index}`}
                            className="animate-pulse rounded-md bg-palette-black-4 p-3 text-xs"
                          >
                            <div className="mb-2 h-4 w-32 rounded bg-palette-black-2"></div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <div className="mb-1 h-3 w-12 rounded bg-palette-black-2"></div>
                                <div className="h-8 w-full rounded border border-palette-border bg-palette-black-1"></div>
                              </div>
                              <div>
                                <div className="mb-1 h-3 w-16 rounded bg-palette-black-2"></div>
                                <div className="h-8 w-full rounded border border-palette-border bg-palette-black-1"></div>
                              </div>
                            </div>
                            <div className="mt-1.5 flex w-full justify-end">
                              <div className="h-4 w-16 rounded bg-palette-black-2"></div>
                            </div>
                          </li>
                        ))
                    : null
                  : (underlyingStocks || []).map((stock, index) => {
                      return (
                        <li
                          key={`stock-${index}`}
                          className="rounded-md bg-palette-black-4 p-3 text-xs"
                        >
                          <div>
                            <div className="mb-2 flex flex-col justify-end font-semibold text-palette-black-7">
                              Underlying Stock
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="">
                                <label className="leadin-none mb-1 block font-semibold">
                                  Price
                                </label>
                                <div>
                                  <input
                                    className="w-full rounded border border-palette-border bg-palette-black-1 px-2 py-1"
                                    type="number"
                                    value={parseFloat(stock.price.toFixed(2))}
                                    onChange={(e) => {
                                      setCustomStrategyName();

                                      const priceValue = e.target.value;

                                      // If field is empty, use current spot price as default
                                      let price: number;
                                      if (!priceValue || priceValue.trim() === '') {
                                        price = spot || 100; // Fallback to 100 if spot is also 0
                                      } else {
                                        const parsedPrice = parseFloat(priceValue);
                                        // Skip update if parseFloat results in NaN or negative number
                                        if (isNaN(parsedPrice) || parsedPrice <= 0) {
                                          return;
                                        }
                                        price = parsedPrice;
                                      }

                                      updateUnderlyingStockAtIndex(index, {
                                        ...stock,
                                        price: price,
                                      });
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="">
                                <label className="leadin-none mb-1 block font-semibold">
                                  Quantity
                                </label>
                                <div>
                                  <input
                                    className="w-full rounded border border-palette-border bg-palette-black-1 px-2 py-1"
                                    type="number"
                                    value={stock.quantity}
                                    onChange={(e) => {
                                      setCustomStrategyName();

                                      updateUnderlyingStockAtIndex(index, {
                                        ...stock,
                                        quantity: parseInt(e.target.value, 10),
                                      });
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="mt-1.5 flex w-full justify-end gap-3 pr-1">
                              <button
                                className="flex items-center font-medium leading-none text-red-400 hover:text-red-500 focus:outline-none"
                                onClick={() => {
                                  setCustomStrategyName();
                                  removeUnderlyingStockAtIndex(index);
                                }}
                                title="Delete Stock"
                              >
                                <span className="mr-1 text-lg leading-none">&times;</span> Remove
                              </button>
                            </div>
                          </div>
                        </li>
                      );
                    })}
              </ul>
            </div>
            <RightSidePanel
              ticker={ticker}
              currentStockPrice={spot}
              isExpanded={splitLayout.isExpanded('bottom')}
              onSetExpanded={handleSetBottomExpanded}
              className={getSinglePaneClass('bottom', splitLayout.expandedPane)}
            />
          </aside>
        </div>
        <ListStrategyModal
          tickerData={tickerData}
          userId={viewer?.viewer?.uid ?? ''}
          expirationDate={addDays(new Date(), -1).toISOString()}
          isOpen={isListStrategyModalOpen}
          handleClose={() => setIsListStrategyModalOpen(false)}
          onStrategySelect={handleStrategySelect}
          handleDelete={handleDeleteStrategy}
        />
        {results && (
          <SaveStrategyModal
            isOpen={isSaveModalOpen}
            handleClose={() => setIsSaveModalOpen(false)}
            strategy={formatStrategyName(strategyType as string)}
            userId={viewer?.viewer?.uid || ''}
            stockPrice={spot}
            stockOptions={stockOptions}
            underlyingStocks={underlyingStocks}
            ticker={ticker}
            riskFreeRate={riskFreeRate}
            normalizedProfitFactor={normalizedProfitFactor}
            netCreditDebit={strategyMetrics?.netDebit || 0}
            impliedVolatility={isManualVolatility ? null : originalStrategyVolatility}
            volatilityAssumption={
              isManualVolatility && volatility != null
                ? `Valuation uses manual / estimated volatility ${(volatility * 100).toFixed(
                    1,
                  )}%, not solved IV.`
                : undefined
            }
            strategyBuilder={results}
            strategyMetrics={strategyMetrics || ({} as any)}
            maxProfit={maxProfit || 0}
            maxLoss={maxLoss || 0}
            probabilityOfProfit={probabilityOfProfit || 0}
            delta={strategyMetrics?.delta || 0}
            gamma={strategyMetrics?.gamma || 0}
            theta={strategyMetrics?.theta || 0}
            vega={strategyMetrics?.vega || 0}
            rho={strategyMetrics?.rho || 0}
            breakEvenPrices={breakEvenPoints || []}
          />
        )}
        <DialogComponent onClose={() => setIsOpen(false)} className="relative z-50" isOpen={isOpen}>
          <div className="relative transform overflow-hidden rounded-lg bg-palette-black-2 text-left shadow-xl transition-all sm:w-full sm:max-w-6xl">
            <div className="mx-auto grid w-full grid-cols-1">
              <div className="grid max-h-[calc(98vh_-_360px)] grid-cols-1 overflow-y-auto px-4 pb-8 md:grid-cols-2 lg:grid-cols-4">
                {['Novice', 'Intermediate', 'Advanced', 'Expert'].map((level) => (
                  <div key={level} className="flex flex-col">
                    <h3 className="sticky top-0 z-10 bg-palette-black-2 px-2 py-2 text-xl font-medium text-white">
                      {level}
                    </h3>
                    <div className="flow-root">
                      {strategies
                        .filter((strategy) => strategy.skillLevel === level)
                        .map((strategy) => (
                          <div key={strategy.slug} className="flex gap-x-2 px-2 py-1">
                            <Link
                              href={getOpcStrategyPage(strategy.slug, ticker)}
                              key={strategy.slug}
                              className="flex cursor-pointer gap-x-2 py-0.5 text-xs text-[#ddd] hover:text-white"
                              onClick={() => {
                                setIsOpen(false);
                                clearStrategyName();
                              }}
                              onMouseEnter={() => openStrategyPanel(strategy?.slug)}
                            >
                              <span className="text-sm">{strategy.name}</span>
                              {showIcon(strategy.slug)}
                            </Link>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
              {strategyData.length ? (
                <div className="bg-palette-black-2 text-white">
                  <StrategyCard strategy={strategyData[0]} ticker={ticker} />
                </div>
              ) : null}
            </div>
          </div>
        </DialogComponent>
        <Modal isOpen={showAuthModal} handleClose={() => setShowAuthModal(false)}>
          <div className="relative rounded-lg bg-palette-black-1 p-3">
            <button onClick={() => setShowAuthModal(false)} className="absolute right-3 top-2">
              <XIcon className="w-5" />
            </button>
            <div>
              <div className="relative max-h-24 overflow-hidden">
                <div className="absolute bottom-0 left-0 h-40 w-full bg-gradient-to-b from-palette-black-4/0 to-palette-black-4">
                  &nbsp;
                </div>
              </div>
              <div className="space-y-4 py-8 text-center text-gray-400">
                <FaLock className="mr-2 inline-block size-8 align-middle" />
                <div className="font-semibold">You must log in to refresh prices</div>
                <div className="text-pretty px-4 text-lg font-normal text-brand-green-primary">
                  Create a free account or{' '}
                  <Link href={`${LOGIN_PAGE}?next=${router.asPath}`} className="underline">
                    login
                  </Link>{' '}
                  to access real-time price updates.
                </div>
                <div className="flex justify-center pt-2">
                  <Link
                    role="button"
                    href={`${SIGNUP_PAGE}?next=${router.asPath}`}
                    className="flex items-center justify-center rounded-md border border-transparent bg-brand-primary px-8 py-2 text-sm font-semibold text-white no-underline"
                  >
                    <span className="mr-2">Get Free Access</span>
                    <IoArrowRedoOutline />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </Modal>
        {/* Mobile Floating Action Button */}
        <button
          onClick={() => setIsMobilePositionsModalOpen(true)}
          className="fixed bottom-5 right-4 z-40 flex h-12 items-center gap-2 rounded-full bg-brand-primary py-2 pl-4 pr-2 shadow-lg lg:hidden"
        >
          <span className="text-sm font-semibold text-white">Positions</span>
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20">
            <PencilIcon className="h-4 w-4 text-white" />
          </span>
          {(stockOptions?.length > 0 || underlyingStocks?.length > 0) && (
            <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-yellow-400 text-xs font-bold text-black">
              {(stockOptions?.length || 0) + (underlyingStocks?.length || 0)}
            </span>
          )}
        </button>
        {/* Mobile Positions Modal */}
        <Modal
          isOpen={isMobilePositionsModalOpen}
          handleClose={() => setIsMobilePositionsModalOpen(false)}
          className="bg-palette-black-1"
          classNameMaxWidth="sm:max-w-lg"
        >
          <div className="flex max-h-[90vh] flex-col bg-palette-black-1">
            {/* Mobile Header */}
            <div className="sticky top-0 z-10 border-b border-palette-border bg-palette-black-1 px-4 pb-4 pt-3">
              {/* Title Row */}
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-base font-semibold">Positions</h2>
                <button
                  onClick={() => setIsMobilePositionsModalOpen(false)}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-palette-black-3 text-gray-400"
                >
                  <XIcon className="h-5 w-5" />
                </button>
              </div>
              {/* Actions Row */}
              <div className="flex items-center justify-between">
                <PositionsRefreshControl
                  variant="mobile"
                  hasPaidAccess={hasPaidAccess}
                  isUserSession={isUserSession}
                  isBusy={isBusy || isFakeSpinning}
                  isLiveUpdatesEnabled={isLiveUpdatesEnabled}
                  onToggleLiveUpdates={setIsLiveUpdatesEnabled}
                  onRefresh={() => refreshData(ticker)}
                  onLoginRequired={() => {
                    setShowAuthModal(true);
                    setIsMobilePositionsModalOpen(false);
                  }}
                />
                <div className="flex gap-2">
                  <button
                    className="rounded-lg bg-yellow-400 px-4 py-2 text-sm font-bold text-black disabled:bg-gray-500"
                    onClick={() => {
                      setIsSaveModalOpen(true);
                      setIsMobilePositionsModalOpen(false);
                    }}
                    disabled={isBusy || !results}
                    title={!results ? 'Choose a usable volatility before saving.' : undefined}
                  >
                    Save
                  </button>
                  <button
                    className="rounded-lg bg-yellow-400 px-4 py-2 text-sm font-bold text-black disabled:bg-gray-500"
                    onClick={() => {
                      handleLoadStrategy();
                      setIsMobilePositionsModalOpen(false);
                    }}
                    disabled={isBusy}
                  >
                    Load
                  </button>
                </div>
              </div>
              {/* Quick Action Buttons */}
              <div className="mt-4 grid grid-cols-5 gap-1">
                <button
                  type="button"
                  title="Buy Call"
                  className="flex items-center justify-center rounded-l-lg bg-brand-primary py-3 text-xs font-medium"
                  onClick={() => {
                    setCustomStrategyName();
                    insertStockOption(
                      convertStrategyParamsToStockOptions(
                        longCall({
                          chain: chain?.[ticker]?.entries,
                          spot: spot,
                          expirationDate: expirationDate,
                          expirationKey: expirationKey,
                        }).optionPositions.sort(strategyBuilderSortFunction) || [],
                      ),
                    );
                  }}
                >
                  Call <FaArrowUp className="ms-1 h-3" />
                </button>
                <button
                  type="button"
                  title="Sell Call"
                  className="flex items-center justify-center bg-brand-primary py-3 text-xs font-medium"
                  onClick={() => {
                    setCustomStrategyName();
                    insertStockOption(
                      convertStrategyParamsToStockOptions(
                        shortCall({
                          chain: chain?.[ticker]?.entries,
                          spot: spot,
                          expirationDate: expirationDate,
                          expirationKey: expirationKey,
                        }).optionPositions.sort(strategyBuilderSortFunction) || [],
                      ),
                    );
                  }}
                >
                  Call <FaArrowDown className="ms-1 h-3" />
                </button>
                <button
                  type="button"
                  title="Buy Put"
                  className="flex items-center justify-center bg-brand-primary py-3 text-xs font-medium"
                  onClick={() => {
                    setCustomStrategyName();
                    insertStockOption(
                      convertStrategyParamsToStockOptions(
                        longPut({
                          chain: chain?.[ticker]?.entries,
                          spot: spot,
                          expirationDate: expirationDate,
                          expirationKey: expirationKey,
                        }).optionPositions.sort(strategyBuilderSortFunction) || [],
                      ),
                    );
                  }}
                >
                  Put <FaArrowUp className="ms-1 h-3" />
                </button>
                <button
                  type="button"
                  title="Sell Put"
                  className="flex items-center justify-center bg-brand-primary py-3 text-xs font-medium"
                  onClick={() => {
                    setCustomStrategyName();
                    insertStockOption(
                      convertStrategyParamsToStockOptions(
                        shortPut({
                          chain: chain?.[ticker]?.entries,
                          spot: spot,
                          expirationDate: expirationDate,
                          expirationKey: expirationKey,
                        }).optionPositions.sort(strategyBuilderSortFunction) || [],
                      ),
                    );
                  }}
                >
                  Put <FaArrowDown className="ms-1 h-3" />
                </button>
                <button
                  type="button"
                  title="Add Underlying Stock"
                  className="flex items-center justify-center rounded-r-lg bg-brand-primary py-3 text-xs font-medium"
                  onClick={() => {
                    setCustomStrategyName();
                    insertUnderlyingStock([
                      {
                        price: spot,
                        quantity: UNDERLYING_STOCK_POSITION_STEPPER,
                      },
                    ]);
                  }}
                >
                  Stock <FiActivity className="ms-1 h-3" />
                </button>
              </div>
            </div>

            {/* Positions List */}
            <ul
              role="list"
              className="flex min-h-0 flex-auto flex-col gap-2 overflow-y-auto px-4 py-3"
            >
              {isBusy
                ? Array(3)
                    .fill(0)
                    .map((_, index) => (
                      <li
                        key={`mobile-skeleton-${index}`}
                        className="animate-pulse rounded-lg border border-palette-border bg-palette-black-4 p-4"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="h-5 w-8 rounded bg-palette-black-2"></div>
                            <div className="h-5 w-14 rounded bg-palette-black-2"></div>
                            <div className="h-5 w-10 rounded bg-palette-black-2"></div>
                          </div>
                          <div className="h-9 w-9 rounded-lg bg-palette-black-2"></div>
                        </div>
                      </li>
                    ))
                : stockOptions?.map((option, index) => {
                    const highlightClass =
                      index === lastAddedIndex ? '!border-graph-green !border-2' : '';
                    const isLong = option.numberOfContracts >= 0;
                    const strikeDirection = isLong ? 'ask' : 'bid';
                    const isCallOption = option.type === CALL;
                    const isMobileEditing = mobileEditingOptionIndex === index;

                    return (
                      <React.Fragment key={`mobile-option-${index}`}>
                        {isMobileEditing ? (
                          <li
                            className={classNames(
                              '!mb-0 rounded-lg border p-3 text-sm',
                              isCallOption
                                ? 'border-graph-green bg-green-950/20'
                                : 'border-graph-red bg-red-950/20',
                            )}
                          >
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="mb-1.5 block text-xs font-semibold text-gray-400">
                                  Type
                                </label>
                                <select
                                  className="w-full rounded-lg border border-palette-border bg-palette-black-1 px-3 py-2 text-sm"
                                  value={option.type}
                                  onChange={(e) => {
                                    setCustomStrategyName();
                                    const newOptionType = e.target.value as OptionType;
                                    const availableStrikes = getAvailableStrikes(
                                      chain,
                                      ticker,
                                      option.expirationKey,
                                      newOptionType as 'CALL' | 'PUT',
                                    );
                                    let newStrike = option.strike;
                                    if (
                                      availableStrikes.length > 0 &&
                                      !availableStrikes.includes(option.strike)
                                    ) {
                                      const targetPrice = option.strike || spot;
                                      newStrike = availableStrikes.reduce((prev, curr) =>
                                        Math.abs(curr - targetPrice) < Math.abs(prev - targetPrice)
                                          ? curr
                                          : prev,
                                      );
                                    }
                                    const premiums = chain?.[
                                      selectedTicker?.t || DEFAULT_TICKER
                                    ]?.entries?.find((entry) => entry.e === option.expirationKey)
                                      ?.c?.[newStrike];
                                    const isCall = newOptionType === CALL;
                                    const premiumsForType = isCall ? premiums?.c : premiums?.p;
                                    const premium = getPremium({
                                      ask: premiumsForType?.a!,
                                      bid: premiumsForType?.b!,
                                      strikeDirection,
                                    });
                                    updateStockOptionAtIndex(index, {
                                      ...option,
                                      type: newOptionType,
                                      strike: newStrike,
                                      premium,
                                    });
                                  }}
                                >
                                  <option value={CALL}>Call</option>
                                  <option value={PUT}>Put</option>
                                </select>
                              </div>
                              <div>
                                <label className="mb-1.5 block text-xs font-semibold text-gray-400">
                                  Expiration
                                </label>
                                <DateDropdown
                                  chain={chain as OptionChain}
                                  ticker={ticker}
                                  defaultValue={option.expirationKey}
                                  value={option.expirationKey}
                                  setValue={(entry) => {
                                    setCustomStrategyName();
                                    const availableStrikes = getAvailableStrikes(
                                      chain,
                                      ticker,
                                      entry.e!,
                                      option.type as 'CALL' | 'PUT',
                                    );
                                    let newStrike = option.strike;
                                    if (
                                      availableStrikes.length > 0 &&
                                      !availableStrikes.includes(option.strike)
                                    ) {
                                      const targetPrice = option.strike || spot;
                                      newStrike = availableStrikes.reduce((prev, curr) =>
                                        Math.abs(curr - targetPrice) < Math.abs(prev - targetPrice)
                                          ? curr
                                          : prev,
                                      );
                                    }
                                    const premiums = entry?.c?.[newStrike];
                                    const isCall = option.type === CALL;
                                    const premiumsForType = isCall ? premiums?.c : premiums?.p;
                                    const premium = getPremium({
                                      ask: premiumsForType?.a!,
                                      bid: premiumsForType?.b!,
                                      strikeDirection,
                                    });
                                    updateStockOptionAtIndex(index, {
                                      ...option,
                                      strike: newStrike,
                                      premium,
                                      expirationKey: entry.e!,
                                      expirationDateString: dateToDateString(
                                        expirationToDate(entry.ey!, entry.em!, entry.ed!),
                                      ),
                                    });
                                  }}
                                />
                              </div>
                              <div>
                                <label className="mb-1.5 block text-xs font-semibold text-gray-400">
                                  Quantity
                                </label>
                                <input
                                  className="w-full rounded-lg border border-palette-border bg-palette-black-1 px-3 py-2 text-sm"
                                  type="number"
                                  value={option.numberOfContracts}
                                  onChange={(e) => {
                                    setCustomStrategyName();
                                    updateStockOptionAtIndex(index, {
                                      ...option,
                                      numberOfContracts: parseFloat(e.target.value || '0'),
                                    });
                                  }}
                                />
                              </div>
                              <div>
                                <label className="mb-1.5 block text-xs font-semibold text-gray-400">
                                  Premium
                                </label>
                                <input
                                  className="w-full rounded-lg border border-palette-border bg-palette-black-1 px-3 py-2 text-sm"
                                  type="number"
                                  value={option.premium === 0.01 ? '' : option.premium}
                                  onChange={(e) => {
                                    setCustomStrategyName();
                                    const premiumValue = e.target.value;
                                    let premium: number;
                                    if (!premiumValue || premiumValue.trim() === '') {
                                      premium = 0.01;
                                    } else {
                                      const parsedPremium = parseFloat(premiumValue);
                                      if (isNaN(parsedPremium) || parsedPremium < 0) {
                                        return;
                                      }
                                      premium = parsedPremium;
                                    }
                                    updateStockOptionAtIndex(index, {
                                      ...option,
                                      premium: premium,
                                    });
                                  }}
                                />
                              </div>
                            </div>
                            <div className="mt-3 flex items-center justify-between">
                              <button
                                className="flex items-center font-medium leading-none text-green-300 focus:outline-none"
                                onClick={() => {
                                  setMobileEditingOptionIndex(null);
                                  sortStockOptions();
                                }}
                              >
                                <IoCheckmark className="mr-1 h-4" />
                                Done
                              </button>
                              <div className="flex items-center gap-3">
                                <button
                                  className="flex items-center font-medium leading-none text-gray-300 focus:outline-none"
                                  onClick={() => {
                                    setCustomStrategyName();
                                    insertStockOption([{ ...option }]);
                                  }}
                                >
                                  <IoCopy className="mr-1 h-3" />
                                  Duplicate
                                </button>
                                <button
                                  className="flex items-center font-medium leading-none text-red-400 hover:text-red-500 focus:outline-none"
                                  onClick={() => {
                                    setCustomStrategyName();
                                    setMobileEditingOptionIndex(null);
                                    removeStockOptionAtIndex(index);
                                  }}
                                >
                                  <span className="mr-1 text-lg leading-none">&times;</span>
                                  Delete
                                </button>
                              </div>
                            </div>
                          </li>
                        ) : (
                          <li
                            className={classNames(
                              '!mb-0 flex items-center justify-between rounded-lg border p-3 text-sm',
                              highlightClass,
                              isCallOption
                                ? 'border-graph-green bg-green-950/20'
                                : 'border-graph-red bg-red-950/20',
                            )}
                          >
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="mr-1 flex items-center font-medium">
                                {option.numberOfContracts > 0 && <ArrowUpIcon className="w-3.5" />}
                                {option.numberOfContracts < 0 && (
                                  <ArrowDownIcon className="w-3.5" />
                                )}
                                {option.numberOfContracts === 0 && <span>-</span>}
                                {option.numberOfContracts}
                              </span>
                              <span className="font-semibold">{option.strike}</span>
                              <span
                                className={classNames(
                                  'font-medium',
                                  isCallOption ? 'text-graph-green' : 'text-graph-red',
                                )}
                              >
                                {isCallOption ? 'C' : 'P'}
                              </span>
                              <span className="text-xs text-gray-400">
                                @ ${option.premium.toFixed(2)}
                              </span>
                              <span className="flex items-center text-xs text-gray-500">
                                <CalendarIcon className="mr-1 w-3" />
                                {option.expirationKey}
                              </span>
                            </div>
                            <button
                              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-primary text-white"
                              onClick={() => setMobileEditingOptionIndex(index)}
                            >
                              <PencilIcon className="w-4" />
                            </button>
                          </li>
                        )}
                      </React.Fragment>
                    );
                  })}
              {isBusy
                ? underlyingStocks && underlyingStocks.length > 0
                  ? Array(1)
                      .fill(0)
                      .map((_, index) => (
                        <li
                          key={`mobile-stock-skeleton-${index}`}
                          className="!mb-0 animate-pulse rounded-lg bg-palette-black-4 p-4 text-xs"
                        >
                          <div className="mb-3 h-5 w-32 rounded bg-palette-black-2"></div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="h-10 w-full rounded-lg border border-palette-border bg-palette-black-1"></div>
                            <div className="h-10 w-full rounded-lg border border-palette-border bg-palette-black-1"></div>
                          </div>
                        </li>
                      ))
                  : null
                : (underlyingStocks || []).map((stock, index) => (
                    <li
                      key={`mobile-stock-${index}`}
                      className="rounded-lg bg-palette-black-4 p-4 text-sm"
                    >
                      <div className="mb-3 font-semibold text-palette-black-7">
                        Underlying Stock
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="mb-1.5 block text-xs font-semibold text-gray-400">
                            Price
                          </label>
                          <input
                            className="w-full rounded-lg border border-palette-border bg-palette-black-1 px-3 py-2 text-sm"
                            type="number"
                            value={parseFloat(stock.price.toFixed(2))}
                            onChange={(e) => {
                              setCustomStrategyName();
                              const priceValue = e.target.value;
                              let price: number;
                              if (!priceValue || priceValue.trim() === '') {
                                price = spot || 100;
                              } else {
                                const parsedPrice = parseFloat(priceValue);
                                if (isNaN(parsedPrice) || parsedPrice <= 0) {
                                  return;
                                }
                                price = parsedPrice;
                              }
                              updateUnderlyingStockAtIndex(index, {
                                ...stock,
                                price: price,
                              });
                            }}
                          />
                        </div>
                        <div>
                          <label className="mb-1.5 block text-xs font-semibold text-gray-400">
                            Quantity
                          </label>
                          <input
                            className="w-full rounded-lg border border-palette-border bg-palette-black-1 px-3 py-2 text-sm"
                            type="number"
                            value={stock.quantity}
                            onChange={(e) => {
                              setCustomStrategyName();
                              updateUnderlyingStockAtIndex(index, {
                                ...stock,
                                quantity: parseFloat(e.target.value || '0'),
                              });
                            }}
                          />
                        </div>
                      </div>
                      <div className="mt-3 flex w-full justify-end">
                        <button
                          className="flex items-center font-medium leading-none text-red-400 hover:text-red-500 focus:outline-none"
                          onClick={() => {
                            setCustomStrategyName();
                            removeUnderlyingStockAtIndex(index);
                          }}
                          title="Remove Stock"
                        >
                          <span className="mr-1 text-lg leading-none">&times;</span>
                          Remove
                        </button>
                      </div>
                    </li>
                  ))}
              {stockOptions?.length === 0 && underlyingStocks?.length === 0 && !isBusy && (
                <li className="flex flex-col items-center justify-center py-12 text-center">
                  <p className="text-base text-gray-400">No positions yet</p>
                  <p className="mt-2 text-sm text-gray-500">Tap the buttons above to add options</p>
                </li>
              )}
            </ul>
          </div>
        </Modal>
        <Footer />
      </>
      {!!jsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd }} />
      )}
      <FloatingBox />
    </>
  );
}
